/**
 * TB Supporter — Individual dashboard.
 * Non-financial: awareness, learning, patient-support guidance, community.
 * In-page section nav (global GovLayout sidebar untouched). Real data via
 * existing endpoints; honest empty states; no fabricated numbers.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, BookOpen, HeartHandshake, Megaphone, Library, Users,
  HandHeart as HandHeartIcon, Bookmark, Bell, Bot, User, Settings,
  ArrowRight, BookMarked, MapPin, Stethoscope, MessagesSquare,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { FloatingTbAssistant } from '@/components/portal/FloatingTbAssistant'
import { SupporterRoleNav, type SupporterNavSection } from '@/components/supporter/SupporterRoleNav'
import {
  SupporterStatCard, SupporterSectionCard, SupporterResourceCard,
  SupporterCampaignCard, SupporterEmptyState,
} from '@/components/supporter/SupporterCards'
import { SupporterProfilePanel } from '@/components/supporter/SupporterProfilePanel'
import { useAuthStore } from '@/stores/authStore'
import notificationService, { type AppNotification } from '@/services/notificationService'
import {
  saveResource, unsaveResource, getSavedResources,
  type Resource, type SavedResourceItem,
} from '@/services/resourceService'
import campaignService, { type Campaign } from '@/services/campaignService'
import {
  useSupporterActivity, fetchActiveCampaigns, fetchResources,
  shareLink, campaignShareUrl, resourceShareUrl,
} from './supporterData'
import { AWARENESS_CAMPAIGN_TYPES, MEDICAL_DISCLAIMER, COLLABORATION_OPTIONS } from './supporterContent'
import { SupporterCollaborationForm } from '@/components/supporter/SupporterCollaborationForm'

const SECTIONS: SupporterNavSection[] = [
  { key: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'learn', label: 'Learn About TB', icon: BookOpen },
  { key: 'support', label: 'Support a Patient', icon: HeartHandshake },
  { key: 'campaigns', label: 'Awareness Campaigns', icon: Megaphone },
  { key: 'resources', label: 'TB Resources', icon: Library },
  { key: 'community', label: 'Community', icon: Users },
  { key: 'volunteer', label: 'Volunteer Opportunities', icon: HandHeartIcon },
  { key: 'saved', label: 'Saved Resources', icon: Bookmark },
  { key: 'collaboration', label: 'Collaboration', icon: MessagesSquare },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'assistant', label: 'TB Assistant', icon: Bot },
  { key: 'profile', label: 'My Profile', icon: User },
  { key: 'settings', label: 'Settings', icon: Settings },
]

const LEARN_TOPICS = [
  { label: 'TB Basics', to: '/knowledge' },
  { label: 'TB Symptoms', to: '/knowledge' },
  { label: 'Diagnosis', to: '/knowledge' },
  { label: 'Treatment Basics', to: '/knowledge' },
  { label: 'Treatment Adherence', to: '/knowledge' },
  { label: 'TB Myths & Facts', to: '/knowledge' },
  { label: 'Nutrition & TB', to: '/knowledge' },
  { label: 'Stigma & Mental Wellbeing', to: '/knowledge' },
  { label: 'Prevention', to: '/knowledge' },
  { label: 'Family Support', to: '/knowledge' },
]

const SUPPORT_GUIDES = [
  { title: 'Communicate respectfully', body: 'Listen without judgement. Use supportive, non-stigmatising language and respect the person\'s privacy.' },
  { title: 'Reduce stigma', body: 'TB is treatable. Speak about it openly and factually so others feel safe seeking help.' },
  { title: 'Encourage treatment adherence', body: 'Gently remind about daily medication and follow-up. Help them remember appointments without pressure.' },
  { title: 'How families can support', body: 'Families can help with nutrition, reminders, rest and emotional support during the full treatment course.' },
  { title: 'Nutrition awareness', body: 'A balanced diet supports recovery. Share official nutrition guidance — do not prescribe diets.' },
  { title: 'When to encourage professional help', body: 'If symptoms worsen, side effects appear, or treatment is interrupted, guide them to a qualified healthcare professional.' },
  { title: 'Find official TB services', body: 'Direct them to the nearest public TB centre / Ni-kshay facility for testing and treatment.' },
  { title: 'What not to say', body: 'Avoid myths like "TB is incurable" or "isolate forever". Avoid giving medical advice or pushing remedies.' },
  { title: 'Myths families should avoid', body: 'TB is not spread by sharing food, and it is curable. Do not delay diagnosis out of fear or stigma.' },
]

const IndividualSupporterDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const activity = useSupporterActivity()

  const [active, setActive] = useState('overview')
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [saved, setSaved] = useState<SavedResourceItem[]>([])
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set())
  const [joinedIds, setJoinedIds] = useState<Set<number>>(new Set())
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loadingList, setLoadingList] = useState(true)

  const fullName = user?.fullName || (user ? `${user.firstName} ${user.lastName}`.trim() : '') || t('supporter.hero.friend', 'friend')

  // Base data load
  useEffect(() => {
    let active0 = true
    setLoadingList(true)
    Promise.allSettled([
      fetchActiveCampaigns(AWARENESS_CAMPAIGN_TYPES, 12),
      fetchResources({ topic: 'AWARENESS', size: 8 }),
      campaignService.getMyCampaigns({ size: 50 }),
      fetchSaved(),
    ]).then(([c, r, mine]) => {
      if (!active0) return
      setCampaigns(c.status === 'fulfilled' ? c.value : [])
      setResources(r.status === 'fulfilled' ? r.value : [])
      if (mine.status === 'fulfilled') {
        setJoinedIds(new Set((mine.value.content ?? []).map((cm) => cm.id)))
      }
      setLoadingList(false)
    })
    return () => { active0 = false }
  }, [])

  const fetchSaved = useCallback(async () => {
    try {
      const page = await getSavedResources(0, 50)
      const items = page.content ?? []
      setSaved(items)
      setSavedIds(new Set(items.map((i) => i.id)))
    } catch {
      setSaved([])
    }
  }, [])

  // Notifications load when section active
  useEffect(() => {
    if (active !== 'notifications') return
    notificationService.getNotifications(0, 20).then((p) => setNotifications(p.content ?? [])).catch(() => setNotifications([]))
  }, [active])

  const toggleSave = useCallback(async (r: Resource) => {
    setSavedIds((prev) => {
      const next = new Set(prev)
      if (next.has(r.id)) next.delete(r.id)
      else next.add(r.id)
      return next
    })
    try {
      if (savedIds.has(r.id)) await unsaveResource(r.id)
      else await saveResource(r.id)
      await fetchSaved()
    } catch {
      await fetchSaved()
    }
  }, [savedIds, fetchSaved])

  const joinCampaign = useCallback(async (c: Campaign) => {
    setJoinedIds((prev) => new Set(prev).add(c.id))
    try { await campaignService.participate(c.id) } catch { setJoinedIds((prev) => { const n = new Set(prev); n.delete(c.id); return n }) }
  }, [])

  const onShareCampaign = useCallback((c: Campaign) => { shareLink(campaignShareUrl(c), c.title) }, [])
  const onShareResource = useCallback((r: Resource) => { shareLink(resourceShareUrl(r), r.title) }, [])

  const statCards = useMemo(() => [
    { icon: BookMarked, value: activity.resourcesSaved, labelKey: 'supporter.individual.stats.resourcesViewed', label: 'Resources Saved', accent: 'bg-cyan-50 text-cyan-600' },
    { icon: BookOpen, value: 0, labelKey: 'supporter.individual.stats.modulesCompleted', label: 'Learning Modules Completed', dataRequired: true, accent: 'bg-blue-50 text-blue-600' },
    { icon: Megaphone, value: activity.campaignsJoined, labelKey: 'supporter.individual.stats.campaignsJoined', label: 'Campaigns Joined', accent: 'bg-emerald-50 text-emerald-600' },
    { icon: Users, value: 0, labelKey: 'supporter.individual.stats.communityActivities', label: 'Community Activities', dataRequired: true, accent: 'bg-violet-50 text-violet-600' },
  ], [activity])

  const primaryActions = [
    { label: t('supporter.individual.actionLearn', 'Learn About TB'), icon: BookOpen, onClick: () => setActive('learn') },
    { label: t('supporter.individual.actionJoin', 'Join an Awareness Campaign'), icon: Megaphone, onClick: () => setActive('campaigns') },
    { label: t('supporter.individual.actionSupport', 'Support a Patient With Knowledge'), icon: HeartHandshake, onClick: () => setActive('support') },
    { label: t('supporter.individual.actionExplore', 'Explore TB Resources'), icon: Library, onClick: () => setActive('resources') },
  ]

  return (
    <GovLayout>
      <FloatingTbAssistant />
      <div className="space-y-5">
        <GovPageTitle
          title={t('supporter.individual.title', 'Welcome back, {{name}}', { name: fullName })}
          subtitle={t('supporter.individual.subtitle', 'Together, we can build a TB-aware and TB-free India.')}
          icon={HeartHandshake}
        />

        {/* Hero actions */}
        <GovCard>
          <div className="flex flex-wrap items-center gap-2.5">
            <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5">
              <Bot className="w-4 h-4" /> {t('supporter.hero.tbAssistant', 'TB Assistant')}
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => setActive('notifications')} className="inline-flex items-center gap-1.5">
              <Bell className="w-4 h-4" /> {t('supporter.hero.notifications', 'Notifications')}
              {activity.unreadCount > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-500 text-white">{activity.unreadCount}</span>}
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => navigate(`/profile/${user?.username}`)} className="inline-flex items-center gap-1.5">
              <User className="w-4 h-4" /> {t('supporter.hero.profile', 'Profile')}
            </GovButton>
            <span className="text-xs text-gray-400 ml-auto">{t('supporter.individual.nonFinancial', 'A free awareness & knowledge platform — no payments.')}</span>
          </div>
        </GovCard>

        <SupporterRoleNav sections={SECTIONS} active={active} onChange={setActive} />

        {/* ── Overview ── */}
        {active === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {statCards.map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}
            </div>
            <SupporterSectionCard title={t('supporter.individual.primaryActions', 'What you can do today')} icon={LayoutDashboard}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {primaryActions.map((a) => {
                  const Icon = a.icon
                  return (
                    <button key={a.label} onClick={a.onClick} className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors text-left min-h-[56px]">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-blue-600" /></div>
                      <span className="font-medium text-gray-900 text-sm flex-1 min-w-0">{a.label}</span>
                      <ArrowRight className="w-4 h-4 text-blue-500 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </SupporterSectionCard>
            <div className="grid lg:grid-cols-2 gap-4">
              <SupporterSectionCard title={t('supporter.individual.recommendedLearning', 'Recommended learning')} icon={BookOpen} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('learn')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : resources.length === 0
                  ? <SupporterEmptyState icon="📚" title={t('supporter.individual.emptyLearn', 'Start your TB learning journey')} actionLabel={t('supporter.individual.actionLearn', 'Learn About TB')} onAction={() => setActive('learn')} />
                  : <div className="space-y-3">{resources.slice(0, 3).map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
              </SupporterSectionCard>
              <SupporterSectionCard title={t('supporter.individual.activeCampaigns', 'Active awareness campaigns')} icon={Megaphone} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('campaigns')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : campaigns.length === 0
                  ? <SupporterEmptyState icon="📣" title={t('supporter.individual.emptyCampaigns', "You haven't joined a campaign yet")} actionLabel={t('supporter.individual.actionExplore', 'Explore Campaigns')} onAction={() => setActive('campaigns')} />
                  : <div className="space-y-3">{campaigns.slice(0, 3).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
              </SupporterSectionCard>
            </div>
          </div>
        )}

        {/* ── Learn About TB ── */}
        {active === 'learn' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.individual.learnTopics', 'Learning topics')} description={t('supporter.individual.learnDesc', 'Explore trusted TB knowledge at your own pace.')} icon={BookOpen}>
              <div className="flex flex-wrap gap-2">
                {LEARN_TOPICS.map((tp) => (
                  <Link key={tp.label} to={tp.to} className="px-3 py-2 rounded-full bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-sm text-gray-700 transition-colors min-h-[40px] flex items-center">{tp.label}</Link>
                ))}
              </div>
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.individual.recommendedReading', 'Recommended reading')} icon={BookMarked}>
              {loadingList ? <SkeletonRows /> : resources.length === 0
                ? <SupporterEmptyState icon="📚" title={t('supporter.individual.emptyLearn', 'Start your TB learning journey')} />
                : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* ── Support a Patient ── */}
        {active === 'support' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.individual.supportTitle', 'How you can support someone with TB')} description={MEDICAL_DISCLAIMER} icon={HeartHandshake}>
              <div className="grid sm:grid-cols-2 gap-3">
                {SUPPORT_GUIDES.map((g) => (
                  <GovCard key={g.title}>
                    <h4 className="font-semibold text-gray-900 text-sm">{g.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{g.body}</p>
                  </GovCard>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <GovButton variant="primary" size="sm" onClick={() => navigate('/find-tb-centre')} className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {t('supporter.individual.findCentre', 'Find a TB Centre')}</GovButton>
                <GovButton variant="secondary" size="sm" onClick={() => navigate('/support-a-patient')} className="inline-flex items-center gap-1.5"><HeartHandshake className="w-4 h-4" /> {t('supporter.individual.supportPatient', 'Support-a-Patient')}</GovButton>
                <GovButton variant="secondary" size="sm" onClick={() => navigate('/symptom-checker')} className="inline-flex items-center gap-1.5"><Stethoscope className="w-4 h-4" /> {t('supporter.individual.symptomChecker', 'Symptom Checker')}</GovButton>
              </div>
            </SupporterSectionCard>
          </div>
        )}

        {/* ── Campaigns ── */}
        {active === 'campaigns' && (
          <SupporterSectionCard title={t('supporter.individual.campaignsTitle', 'Awareness campaigns')} description={t('supporter.individual.campaignsDesc', 'Joining means awareness participation — no payment or sponsorship.')} icon={Megaphone}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="📣" title={t('supporter.individual.emptyCampaigns', "You haven't joined a campaign yet")} actionLabel={t('supporter.individual.actionExplore', 'Explore Campaigns')} onAction={() => navigate('/campaigns')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
          </SupporterSectionCard>
        )}

        {/* ── Resources ── */}
        {active === 'resources' && (
          <SupporterSectionCard title={t('supporter.individual.resourcesTitle', 'TB resources')} description={t('supporter.individual.resourcesDesc', 'Trusted awareness materials — read, save and share.')} icon={Library}>
            {loadingList ? <SkeletonRows /> : resources.length === 0
              ? <SupporterEmptyState icon="🗂️" title={t('supporter.individual.emptyResources', 'No resources available right now')} actionLabel={t('supporter.individual.browseResources', 'Browse Resources')} onAction={() => navigate('/resources')} />
              : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/resources')}>{t('supporter.individual.browseResources', 'Browse all resources')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* ── Community ── */}
        {active === 'community' && (
          <SupporterSectionCard title={t('supporter.individual.communityTitle', 'Community')} description={t('supporter.individual.communityDesc', 'Join discussions, stories and awareness activities.')} icon={Users}>
            <div className="grid sm:grid-cols-2 gap-3">
              <CommunityLink icon={MessagesSquare} label={t('supporter.individual.communityFeed', 'Community feed')} desc={t('supporter.individual.communityFeedDesc', 'Discussions, posts and stories.')} to="/feed" />
              <CommunityLink icon={Megaphone} label={t('supporter.individual.communityCampaigns', 'Awareness activities')} desc={t('supporter.individual.communityCampaignsDesc', 'Join awareness campaigns.')} to="/campaigns" />
              <CommunityLink icon={BookOpen} label={t('supporter.individual.communityKnowledge', 'Knowledge hub')} desc={t('supporter.individual.communityKnowledgeDesc', 'Trusted TB articles.')} to="/knowledge" />
              <CommunityLink icon={Users} label={t('supporter.individual.communityGroups', 'Groups')} desc={t('supporter.individual.communityGroupsDesc', 'Connect with support groups.')} to="/groups" />
            </div>
          </SupporterSectionCard>
        )}

        {/* ── Volunteer ── */}
        {active === 'volunteer' && (
          <SupporterSectionCard title={t('supporter.individual.volunteerTitle', 'Volunteer opportunities')} description={t('supporter.individual.volunteerDesc', 'Non-financial awareness, outreach and content-sharing opportunities.')} icon={HandHeartIcon}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="🤝" title={t('supporter.individual.emptyVolunteer', 'No opportunities right now')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.slice(0, 6).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/volunteer')}>{t('supporter.individual.exploreOpportunities', 'Explore opportunities')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* ── Saved ── */}
        {active === 'saved' && (
          <SupporterSectionCard title={t('supporter.individual.savedTitle', 'My saved resources')} icon={Bookmark}>
            {saved.length === 0
              ? <SupporterEmptyState icon="🔖" title={t('supporter.individual.emptySaved', 'No saved resources yet')} description={t('supporter.individual.emptySavedDesc', 'Save articles, videos and guides to find them here.')} actionLabel={t('supporter.individual.browseResources', 'Browse Resources')} onAction={() => setActive('resources')} />
              : <div className="grid sm:grid-cols-2 gap-3">{saved.map((r) => <SupporterResourceCard key={r.id} resource={r} saved onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
          </SupporterSectionCard>
        )}

        {/* ── Collaboration ── */}
        {active === 'collaboration' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.collaboration.heading', 'Collaboration')} description={t('supporter.collaboration.individualDesc', 'Express interest in awareness, education or community initiatives. This is not a financial sponsorship.')} icon={MessagesSquare}>
              <SupporterCollaborationForm interestOptions={COLLABORATION_OPTIONS.individual} />
            </SupporterSectionCard>
          </div>
        )}

        {/* ── Notifications ── */}
        {active === 'notifications' && (
          <SupporterSectionCard title={t('supporter.individual.notificationsTitle', 'Notifications')} icon={Bell} action={<GovButton variant="ghost" size="sm" onClick={() => notificationService.markAllAsRead().then(() => setNotifications((n) => n.map((x) => ({ ...x, isRead: true }))))}>{t('supporter.individual.markAllRead', 'Mark all read')}</GovButton>}>
            {notifications.length === 0
              ? <SupporterEmptyState icon="🔔" title={t('supporter.individual.emptyNotifications', 'You\'re all caught up')} description={t('supporter.individual.emptyNotificationsDesc', 'No notifications right now.')} />
              : <div className="divide-y divide-gray-100">{notifications.map((n) => (
                  <div key={n.id} className={`py-3 ${n.isRead ? '' : 'bg-blue-50/40 -mx-2 px-2 rounded-lg'}`}>
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-sm text-gray-600">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(n.createdAt).toLocaleString('en-IN')}</p>
                  </div>
                ))}</div>}
          </SupporterSectionCard>
        )}

        {/* ── Assistant ── */}
        {active === 'assistant' && (
          <SupporterSectionCard title={t('supporter.individual.assistantTitle', 'TB Assistant')} description={MEDICAL_DISCLAIMER} icon={Bot}>
            <p className="text-sm text-gray-600">{t('supporter.individual.assistantDesc', 'Discover TB information, symptoms, official resources, TB centres, campaigns, patient-support guidance and nutrition education. The assistant does not replace a doctor.')}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.individual.openAssistant', 'Open TB Assistant')}</GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => navigate('/find-tb-centre')} className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {t('supporter.individual.findCentre', 'Find a TB Centre')}</GovButton>
            </div>
          </SupporterSectionCard>
        )}

        {/* ── Profile ── */}
        {active === 'profile' && (
          <SupporterSectionCard title={t('supporter.individual.profileTitle', 'My profile')} description={t('supporter.individual.profileDesc', 'Manage your details and awareness interests. No medical information required.')} icon={User}>
            <SupporterProfilePanel role="individual" />
          </SupporterSectionCard>
        )}

        {/* ── Settings ── */}
        {active === 'settings' && (
          <SupporterSectionCard title={t('supporter.individual.settingsTitle', 'Settings')} icon={Settings}>
            <p className="text-sm text-gray-600">{t('supporter.individual.settingsDesc', 'Account settings, notifications and language preferences.')}</p>
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/settings')}>{t('supporter.individual.openSettings', 'Open settings')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}
      </div>
    </GovLayout>
  )
}

function SkeletonRows() {
  return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
}

function CommunityLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-blue-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

export default IndividualSupporterDashboard